import { useQuery, useMutation } from '@tanstack/react-query';
import { deleteReduction, getReductions, postReduction } from '../../services/reduction';
import { QueryKey } from '../../enums/QueryKey';
import { queryClient } from '../../services/queryClient';
import dayjs, { Dayjs } from '@utils/dayjs';
import { useDeleteRecord } from './crudOperations';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useDispatchSuccessfullySavedRecord } from '../useDispatchSuccessfullySavedRecord';
import { appTimezone } from '../../config';
import { DATE_FORMATS } from '@callmart/types';

interface ReductionFilterParams {
    location: number | number[];
    worktype: number | number[];
    startDate: string | Date | Dayjs;
    endDate: string | Date | Dayjs;
}

export function getReductionsQueryKey(filterParams: ReductionFilterParams) {
    const { location, worktype, startDate, endDate } = filterParams;

    // Use app timezone for query key consistency
    const start = dayjs(startDate).tz(appTimezone).startOf('month').format(DATE_FORMATS.ISO_DATETIME);
    const end = dayjs(endDate).tz(appTimezone).endOf('month').format(DATE_FORMATS.ISO_DATETIME);

    return [
        QueryKey.Reductions,
        {
            start,
            end,
            ...(location && { location }),
            ...(worktype && { worktype }),
        },
    ];
}

export function useReductions(params: ReductionFilterParams) {
    const { location, worktype, startDate, endDate } = params;

    // Use app timezone (Europe/Berlin) since reductions are displayed in German time
    const start = dayjs(startDate).tz(appTimezone).startOf('month').toISOString();
    const end = dayjs(endDate).tz(appTimezone).endOf('month').toISOString();

    const isValidStart = dayjs(startDate).isValid();
    const isValidEnd = dayjs(endDate).isValid();

    const { data } = useQuery({
        queryKey: getReductionsQueryKey(params),
        queryFn: () =>
            getReductions({
                location,
                worktype,
                start,
                end,
            }),
        initialData: { reductions: [], durationTotal: 0, reductionTotal: 0 },
        enabled:
            isValidStart &&
            isValidEnd &&
            location !== undefined &&
            location !== null &&
            worktype !== undefined &&
            worktype !== null,
    });

    const { reductions, durationTotal, reductionTotal } = data;
    return { reductions, durationTotal, reductionTotal };
}

export function useDeleteReduction(filterParams: ReductionFilterParams) {
    return useDeleteRecord({
        deleteRecordFn: deleteReduction,
        fetchRecordsQueryKey: getReductionsQueryKey(filterParams),
        recordName: 'Abschlag',
    });
}

export function usePostReduction({
    filterParams,
    onSuccess,
}: {
    filterParams: ReductionFilterParams;
    onSuccess: () => void;
}) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation({
        mutationFn: postReduction,
        onSuccess: () => {
            queryClient.invalidateQueries(getReductionsQueryKey(filterParams));
            onSuccess();
            dispatchSuccessfullySavedRecord('Abschlag');
        },
        onError: (error: Error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}
